import { useRef, useState } from "react";

/**
 * Headless HTML5 drag-and-drop reorder hook.
 * Works with any element (table rows, list items, cards).
 *
 * Usage:
 *   const drag = useDragReorder({ items, getId, onReorder });
 *   <tr {...drag.rowProps(item)} className={drag.rowClass(item)}>
 *     <td>{drag.handle()}</td>
 *     ...
 *   </tr>
 *
 * onReorder receives (newItems, newIdsInOrder). Persist to backend from there.
 */
import { GripVertical } from "lucide-react";

export default function useDragReorder({ items, getId, onReorder }) {
  const [draggingId, setDragging] = useState(null);
  const [overId, setOver] = useState(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const rowProps = (item) => {
    const id = getId(item);
    return {
      draggable: true,
      onDragStart: (e) => {
        setDragging(id);
        e.dataTransfer.effectAllowed = "move";
        try { e.dataTransfer.setData("text/plain", id); } catch { /* ignore */ }
      },
      onDragOver: (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (overId !== id) setOver(id);
      },
      onDragLeave: () => setOver(null),
      onDragEnd: () => { setDragging(null); setOver(null); },
      onDrop: (e) => {
        e.preventDefault();
        const src = draggingId;
        setDragging(null); setOver(null);
        if (!src || src === id) return;
        const arr = [...itemsRef.current];
        const sIdx = arr.findIndex((it) => getId(it) === src);
        const tIdx = arr.findIndex((it) => getId(it) === id);
        if (sIdx < 0 || tIdx < 0) return;
        const [moved] = arr.splice(sIdx, 1);
        arr.splice(tIdx, 0, moved);
        onReorder?.(arr, arr.map(getId));
      },
      "data-drag-id": id,
    };
  };

  const rowClass = (item) => {
    const id = getId(item);
    if (draggingId === id) return "opacity-40";
    if (overId === id && draggingId) return "bg-primary/5 ring-1 ring-inset ring-primary/40";
    return "";
  };

  const Handle = ({ testId }) => (
    <button
      type="button"
      aria-label="Drag to reorder"
      className="inline-grid h-8 w-8 cursor-grab place-items-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 active:cursor-grabbing"
      data-testid={testId}
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
    >
      <GripVertical className="h-4 w-4" />
    </button>
  );

  return { rowProps, rowClass, Handle, draggingId, overId };
}
